import { z } from 'zod';
// =============================================================================
// Common Types - Shared across multiple schemas
// =============================================================================
/**
 * MongoDB ObjectId as string
 */
export const ObjectIdSchema = z.string().regex(/^[a-fA-F0-9]{24}$/, 'Invalid ObjectId format');
/**
 * US Address schema
 */
export const AddressSchema = z.object({
    street: z.string().min(1, 'Street is required'),
    unit: z.string().optional(),
    city: z.string().min(1, 'City is required'),
    state: z.string().length(2, 'State must be 2-letter code'),
    zipCode: z.string().regex(/^\d{5}(-\d{4})?$/, 'Invalid ZIP code'),
    country: z.string().default('US'),
});
/**
 * Pagination request parameters
 */
export const PaginationParamsSchema = z.object({
    page: z.coerce.number().int().positive().default(1),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    sortBy: z.string().optional(),
    sortOrder: z.enum(['asc', 'desc']).default('desc'),
});
/**
 * Pagination response metadata
 */
export const PaginationMetaSchema = z.object({
    page: z.number(),
    limit: z.number(),
    total: z.number(),
    totalPages: z.number(),
    hasNextPage: z.boolean(),
    hasPrevPage: z.boolean(),
});
/**
 * Generic paginated response wrapper
 */
export const createPaginatedResponseSchema = (itemSchema) => z.object({
    data: z.array(itemSchema),
    meta: PaginationMetaSchema,
});
/**
 * Phone number (E.164 format)
 */
export const PhoneSchema = z
    .string()
    .regex(/^\+[1-9]\d{1,14}$/, 'Phone must be in E.164 format (e.g., +14155551234)');
/**
 * Email (lowercase)
 */
export const EmailSchema = z.string().email().toLowerCase();
/**
 * Password (v1 policy, 2026-09: length over complexity per NIST SP 800-63B-4 —
 * no composition rules, no expiry. Max 128 guards the hashing path against
 * oversized inputs. Blocklist/identity checks are server-side in
 * validatePasswordStrength, not here.)
 */
export const PasswordSchema = z
    .string()
    .min(12, 'Password must be at least 12 characters')
    .max(128, 'Password must be at most 128 characters');
/**
 * Timestamp fields common to all documents
 */
export const TimestampsSchema = z.object({
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
/**
 * API Error response
 */
export const ApiErrorSchema = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        details: z.record(z.unknown()).optional(),
    }),
});
/**
 * API Success response wrapper
 */
export const createApiResponseSchema = (dataSchema) => z.object({
    success: z.literal(true),
    data: dataSchema,
});
//# sourceMappingURL=common.js.map